import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Flex,
  Text,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  useToast,
  Badge,
  HStack,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { RepeatIcon } from '@chakra-ui/icons';

import { useScenarioData } from './hooks/useScenarioData';
import { useTalukaSelection } from './hooks/useTalukaSelection';
import { useExcelData } from './hooks/useExcelData';
import { useFileActions } from './hooks/useFileActions';

import TalukaPanel from './components/TalukaPanel';
import ExcelGrid from './components/ExcelGrid';
import MonthSelector from './components/MonthSelector';
import ActionButtons from './components/ActionButtons';
import RejectedFileWarning from './components/RejectedFileWarning';

const ExcelEditModal = ({
  isOpen,
  onClose,
  editingFile,
  currentFY,
  onFileUpdate,
}) => {
  const toast = useToast();

  const modalBg = useColorModeValue('white', 'gray.800');
  const headerBg = useColorModeValue(
    'linear-gradient(135deg, #2C7A7B 0%, #38B2AC 100%)',
    'linear-gradient(135deg, #1A4D4E 0%, #2C7A7B 100%)',
  );
  const headerTextColor = 'white';
  const footerBg = useColorModeValue('gray.50', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const successColor = useColorModeValue('green.500', 'green.300');
  const warningColor = useColorModeValue('orange.500', 'orange.300');
  const infoColor = useColorModeValue('blue.500', 'blue.300');
  const dangerColor = useColorModeValue('red.500', 'red.300');

  const [selectedMonth, setSelectedMonth] = useState('');
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [showMonthSelector, setShowMonthSelector] = useState(false);
  const [defaultMonthSet, setDefaultMonthSet] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    loading: scenarioLoading,
    scenario,
    tahsils,
    availableMonths,
    fileType,
    error: scenarioError,
    loadScenario,
  } = useScenarioData(editingFile, currentFY);

  const {
    selectedTalukaIds,
    talukasWithData,
    talukasWithoutData,
    handleTalukaClick,
    handleTalukaCheck,
    selectAllSubmitted,
    areAllSubmittedSelected,
    clearSelection,
  } = useTalukaSelection(tahsils, scenario);

  const {
    loading: excelLoading,
    headers,
    rows,
    hasUnsavedChanges,
    currentFileId,
    files,
    fileId,
    fileRowRanges,
    fileStatuses,
    isEditMode,
    editingFileId,
    showRejectedWarning,
    pendingFileToEdit,
    loadExcelData,
    handleCellEdit,
    saveChanges,
    discardChanges,
    resetData,
    startEditing,
    cancelEditing,
    proceedWithEditing,
    cancelRejectedEdit,
    isFileEditing,
    isAnyFileEditing,
    isCellEdited,
    getFileStatus,
  } = useExcelData(editingFile, currentFY, scenario);

  const {
    approveLoading,
    rejectLoading,
    rejectionComment,
    showRejectInput,
    setRejectionComment,
    setShowRejectInput,
    approveFiles,
    rejectFiles,
    validateFilesForAction,
  } = useFileActions(onFileUpdate, onClose);

  console.log('Please select files to approve:', files);

  // ─── Total edited cells count (shown in header) ──────────────────────────
  const totalEditedCount = useMemo(() => {
    if (!rows || !headers) return 0;
    let count = 0;
    for (let r = 0; r < rows.length; r++) {
      for (let c = 0; c < (rows[r]?.length ?? 0); c++) {
        if (isCellEdited(r, c)) count++;
      }
    }
    return count;
  }, [rows, headers, isCellEdited]);
  // ─────────────────────────────────────────────────────────────────────────

  const selectedFiles = useMemo(() => {
    if (scenario === 'nonTalukaYearly') {
      return files && files.length > 0 ? [files[0]] : [];
    }
    else if (scenario === 'nonTalukaMonthly') {
      if (!selectedMonth || !files || files.length === 0) {
        return [];
      }
      const monthlyFile = files.find(file => {
        return file.month === selectedMonth ||
          file.monthName === selectedMonth ||
          (file.name && file.name.includes(selectedMonth)) ||
          (file.tahsil?.month === selectedMonth) ||
          (file.monthlyData && file.monthlyData.month === selectedMonth);
      });
      return monthlyFile ? [monthlyFile] : (files.length > 0 ? [files[0]] : []);
    }
    else if (scenario?.toLowerCase().includes('sdo')) {
      // For SDO scenarios, the backend already filters by subDivision.
      // All returned files belong to the selected SDO(s), so return them directly.
      if (selectedTalukaIds.length > 0 && files.length > 0) {
        return files;
      }
      return [];
    }
    else {
      // For taluka-based scenarios, match by tahsil ID
      const selectedIdsStr = selectedTalukaIds.map(id => String(id));
      return files.filter(file => {
        const fileTahsilId = String(file.tahsil?._id || '');
        const fileOwnId = String(file._id || '');
        return selectedIdsStr.includes(fileTahsilId) || selectedIdsStr.includes(fileOwnId);
      });
    }
  }, [selectedTalukaIds, files, scenario, selectedMonth]);

  console.log(selectedFiles, 'selectedFiles------------');

  const selectedFileIds = useMemo(() => {
    if (scenario === 'nonTalukaYearly' || scenario === 'nonTalukaMonthly') {
      return selectedFiles.length > 0 && selectedFiles[0]?._id ? [selectedFiles[0]._id] : [];
    }
    return selectedFiles
      .map(file => file._id)
      .filter(id => id);
  }, [selectedFiles, scenario]);

  const getCurrentMonth = useCallback(() => {
    const date = new Date();
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    return monthNames[date.getMonth()];
  }, []);

  useEffect(() => {
    if (isOpen && editingFile) {
      setDefaultMonthSet(false);
      loadScenario(selectedMonth).then((result) => {
        setInitialLoadDone(true);
      });
    }
  }, [isOpen, editingFile, loadScenario, selectedMonth]);

  useEffect(() => {
    if (
      initialLoadDone &&
      (scenario === 'talukaMonthly' || scenario === 'nonTalukaMonthly' || scenario === 'sdoMonthly') &&
      availableMonths.length > 0 &&
      !selectedMonth &&
      !defaultMonthSet
    ) {
      const currentMonth = getCurrentMonth();
      if (availableMonths.includes(currentMonth)) {
        setSelectedMonth(currentMonth);
      } else {
        setSelectedMonth(availableMonths[0]);
      }
      setDefaultMonthSet(true);
    }
  }, [initialLoadDone, scenario, availableMonths, selectedMonth, defaultMonthSet, getCurrentMonth]);

  useEffect(() => {
    if (initialLoadDone && talukasWithData.length > 0 && selectedTalukaIds.length === 0) {
      const firstTaluka = talukasWithData[0];
      handleTalukaClick(firstTaluka._id);
    }
  }, [initialLoadDone, talukasWithData, selectedTalukaIds.length, handleTalukaClick]);

  useEffect(() => {
    if (!scenario) return;

    const shouldLoad =
      (scenario === 'talukaYearly' && selectedTalukaIds.length > 0) ||
      (scenario === 'talukaMonthly' && selectedTalukaIds.length > 0 && selectedMonth) ||
      (scenario === 'sdoMonthly' && selectedTalukaIds.length > 0 && selectedMonth) ||
      ((scenario === 'sdoNonMonthly' || scenario === 'sdoYearly') && selectedTalukaIds.length > 0) ||
      (scenario === 'nonTalukaMonthly' && selectedMonth) ||
      scenario === 'nonTalukaYearly';

    if (shouldLoad) {
      loadExcelData(selectedTalukaIds, selectedMonth);
    }
  }, [scenario, selectedTalukaIds, selectedMonth, loadExcelData]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedMonth('');
      clearSelection();
      resetData();
      setShowRejectInput(false);
      setRejectionComment('');
      setInitialLoadDone(false);
      setShowMonthSelector(false);
      setDefaultMonthSet(false);
      setIsRefreshing(false);
    }
  }, [isOpen, clearSelection, resetData]);

  const handleMonthChange = useCallback((month) => {
    setSelectedMonth(month);
    setShowMonthSelector(false);
  }, []);

  const handleRefresh = useCallback(async () => {
    if (!scenario) return;
    setIsRefreshing(true);
    try {
      await loadScenario(selectedMonth);
      const shouldLoad =
        (scenario === 'talukaYearly' && selectedTalukaIds.length > 0) ||
        (scenario === 'talukaMonthly' && selectedTalukaIds.length > 0 && selectedMonth) ||
        (scenario === 'sdoMonthly' && selectedTalukaIds.length > 0 && selectedMonth) ||
        ((scenario === 'sdoNonMonthly' || scenario === 'sdoYearly') && selectedTalukaIds.length > 0) ||
        (scenario === 'nonTalukaMonthly' && selectedMonth) ||
        scenario === 'nonTalukaYearly';

      if (shouldLoad) {
        await loadExcelData(selectedTalukaIds, selectedMonth, true);
      }
    } catch (error) {
      toast({
        title: 'Refresh failed',
        description: error.message || 'Failed to refresh data',
        status: 'error',
        duration: 5000,
        position: 'top-right',
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [scenario, selectedTalukaIds, selectedMonth, loadScenario, loadExcelData, toast]);

  const handleApprove = useCallback(async () => {
    if (!selectedFiles.length) {
      toast({
        title: 'No records selected',
        description: 'Please select records to approve',
        status: 'warning',
        duration: 3000,
        position: 'top-right',
      });
      return;
    }
    await approveFiles(selectedFileIds, selectedFiles);
    handleRefresh();
  }, [selectedFiles, selectedFileIds, approveFiles, toast, handleRefresh]);

  const handleReject = useCallback(async () => {
    if (!selectedFiles.length) {
      toast({
        title: 'No records selected',
        description: 'Please select records to reject',
        status: 'warning',
        duration: 3000,
        position: 'top-right',
      });
      return;
    }
    await rejectFiles(selectedFileIds, rejectionComment, selectedFiles);
    handleRefresh();
  }, [selectedFiles, selectedFileIds, rejectionComment, rejectFiles, toast, handleRefresh]);

  const handleRejectToggle = useCallback(() => {
    setShowRejectInput((prev) => !prev);
    setRejectionComment('');
  }, []);

  const handleSave = useCallback(async () => {
    const result = await saveChanges();
    if (result?.success && onFileUpdate) {
      onFileUpdate();
    }
    handleRefresh();
  }, [saveChanges, onFileUpdate]);

  const handleDiscard = useCallback(() => {
    discardChanges();
  }, [discardChanges]);

  const getDisplayName = () => {
    if (fileType?.name) return fileType.name;
    if (typeof editingFile === 'string') return 'Loading...';
    return editingFile?.name || 'Edit Excel Data';
  };

  const getScenarioColor = () => {
    if (scenario?.includes('taluka') || scenario?.toLowerCase().includes('sdo')) return 'purple';
    if (scenario?.includes('Monthly')) return 'blue';
    return 'green';
  };

  if (!isOpen) return null;

  if (scenarioLoading && !tahsils.length) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="full">
        <ModalOverlay />
        <ModalContent bg={modalBg}>
          <ModalHeader bg={headerBg} color={headerTextColor}>
            <Flex align="center">
              <Spinner size="sm" mr={3} color="white" />
              Loading Excel Data...
            </Flex>
          </ModalHeader>
          <ModalCloseButton color={headerTextColor} />
          <ModalBody>
            <Center h="400px" flexDirection="column">
              <Spinner size="xl" color={infoColor} thickness="4px" mb={4} />
              <Text color="gray.500">Loading file information...</Text>
            </Center>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  if (scenarioError) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="full">
        <ModalOverlay />
        <ModalContent bg={modalBg}>
          <ModalHeader bg="red.500" color="white">
            Error
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody>
            <Alert
              status="error"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              p={6}
            >
              <AlertIcon boxSize="40px" mr={0} />
              <AlertTitle mt={4} mb={1} fontSize="lg">
                Failed to load data
              </AlertTitle>
              <AlertDescription maxWidth="sm" textAlign="center">
                {scenarioError}
              </AlertDescription>
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <ModalOverlay />

      <RejectedFileWarning
        isOpen={showRejectedWarning}
        onClose={cancelRejectedEdit}
        onConfirm={() => proceedWithEditing(pendingFileToEdit)}
        fileName={
          files.find((f) => f._id === pendingFileToEdit)?.nameOfOffice ||
          files.find((f) => f._id === pendingFileToEdit)?.tahsil?.name ||
          files.find((f) => f._id === pendingFileToEdit)?.sdo?.name ||
          'Selected file'
        }
      />

      <ModalContent bg={modalBg} maxW="98vw" maxH="98vh">
        <ModalHeader bg={headerBg} color={headerTextColor} py={3}>
          <Flex justify="space-between" align="center">
            <Box>
              <Flex align="center" gap={3} flexWrap="wrap">
                <Text fontSize="xl" fontWeight="bold">
                  {getDisplayName()}
                </Text>
              </Flex>

              {/* Month Badges for Monthly Scenarios */}
              {(scenario === 'talukaMonthly' || scenario === 'nonTalukaMonthly' || scenario === 'sdoMonthly') && (
                <HStack mt={2} spacing={2} flexWrap="wrap">
                  <Text fontSize="sm" fontWeight="normal" mr={1}>
                    Month:
                  </Text>
                  {availableMonths.map((month) => {
                    const isCurrentMonth = month === getCurrentMonth();
                    const hasData = true;
                    return (
                      <Badge
                        key={month}
                        colorScheme={
                          selectedMonth === month ? 'yellow' : isCurrentMonth ? 'teal' : 'gray'
                        }
                        cursor={hasData ? 'pointer' : 'not-allowed'}
                        px={3}
                        py={1}
                        borderRadius="full"
                        onClick={hasData ? () => handleMonthChange(month) : undefined}
                        _hover={hasData ? { transform: 'scale(1.05)' } : {}}
                        transition="all 0.2s"
                        bg={
                          selectedMonth === month
                            ? 'yellow.400'
                            : isCurrentMonth && selectedMonth !== month
                              ? 'teal.100'
                              : hasData
                                ? 'gray.200'
                                : 'gray.100'
                        }
                        color={
                          selectedMonth === month
                            ? 'black'
                            : isCurrentMonth && selectedMonth !== month
                              ? 'teal.800'
                              : hasData
                                ? 'gray.700'
                                : 'gray.400'
                        }
                        border={isCurrentMonth && selectedMonth !== month ? '1px solid' : 'none'}
                        borderColor="teal.400"
                        opacity={hasData ? 1 : 0.6}
                      >
                        {month}
                        {isCurrentMonth && selectedMonth !== month && (
                          <Text as="span" ml={1} fontSize="10px">●</Text>
                        )}
                      </Badge>
                    );
                  })}
                </HStack>
              )}
            </Box>

            {/* ── Right side: edited-cells counter + refresh button ── */}
            <HStack spacing={3} marginRight={6} align="center">

              {/* Edited cells counter — only visible in edit mode */}
              {isEditMode && (
                <Tooltip
                  label={
                    totalEditedCount > 0
                      ? `${totalEditedCount} ${totalEditedCount === 1 ? 'cell has' : 'cells have'} been modified`
                      : 'No cells edited yet'
                  }
                  placement="bottom"
                >
                  <HStack
                    spacing={1.5}
                    bg={totalEditedCount > 0 ? 'whiteAlpha.300' : 'whiteAlpha.100'}
                    border="1px solid"
                    borderColor={totalEditedCount > 0 ? 'yellow.300' : 'whiteAlpha.400'}
                    borderRadius="full"
                    px={3}
                    py={1}
                    transition="all 0.3s"
                  >
                    {/* Animated dot indicator */}
                    <Box
                      w="8px"
                      h="8px"
                      borderRadius="full"
                      bg={totalEditedCount > 0 ? 'yellow.300' : 'whiteAlpha.500'}
                      boxShadow={totalEditedCount > 0 ? '0 0 6px 2px rgba(236,201,75,0.6)' : 'none'}
                      transition="all 0.3s"
                    />
                    <Text
                      fontSize="sm"
                      fontWeight="700"
                      color={totalEditedCount > 0 ? 'yellow.200' : 'whiteAlpha.600'}
                      transition="color 0.3s"
                      letterSpacing="0.3px"
                    >
                      {totalEditedCount} {totalEditedCount === 1 ? 'cell' : 'cells'} edited
                    </Text>
                  </HStack>
                </Tooltip>
              )}

              {/* Refresh button */}
              <Tooltip label="Refresh data" placement="bottom">
                <IconButton
                  icon={<RepeatIcon />}
                  size="lg"
                  variant="ghost"
                  colorScheme="white"
                  color="white"
                  onClick={handleRefresh}
                  isLoading={isRefreshing}
                  aria-label="Refresh data"
                  isDisabled={approveLoading || rejectLoading || excelLoading || isRefreshing}
                  _hover={{
                    bg: 'whiteAlpha.300',
                    transform: 'rotate(180deg)',
                  }}
                  transition="all 0.3s"
                />
              </Tooltip>
            </HStack>
            {/* ───────────────────────────────────────────────────────── */}
          </Flex>
        </ModalHeader>
        <ModalCloseButton color={headerTextColor} />

        <ModalBody p={0} overflow="hidden">
          <Flex h="calc(100vh - 140px)">
            {/* Left Panel - Talukas */}
            {(scenario === 'talukaYearly' || scenario === 'talukaMonthly' || scenario === 'sdoMonthly' || scenario === 'sdoNonMonthly' || scenario === 'sdoYearly') && (
              <TalukaPanel
                talukasWithData={talukasWithData}
                talukasWithoutData={talukasWithoutData}
                selectedTalukaIds={selectedTalukaIds}
                onTalukaClick={handleTalukaClick}
                onTalukaCheck={handleTalukaCheck}
                onSelectAllSubmitted={selectAllSubmitted}
                showCheckboxes={true}
                successColor={successColor}
                warningColor={warningColor}
                infoColor={infoColor}
                currentFY={currentFY}
                selectedMonth={selectedMonth}
                fileId={fileId}
                scenario={scenario}
              />
            )}

            {/* Right Panel - Excel Grid */}
            <Box flex={1} overflow="hidden" bg="gray.50">
              <ExcelGrid
                headers={headers}
                rows={rows}
                onCellEdit={handleCellEdit}
                isLoading={excelLoading || isRefreshing}
                hasUnsavedChanges={hasUnsavedChanges}
                onSave={handleSave}
                onDiscard={handleDiscard}
                isSaving={excelLoading}
                successColor={successColor}
                warningColor={warningColor}
                dangerColor={dangerColor}
                infoColor={infoColor}
                borderColor={borderColor}
                isEditMode={isEditMode}
                onStartEdit={startEditing}
                onCancelEdit={cancelEditing}
                editingFileId={editingFileId}
                files={files}
                file={File}
                fileRowRanges={fileRowRanges}
                isFileEditing={isFileEditing}
                isAnyFileEditing={isAnyFileEditing}
                isCellEdited={isCellEdited}
                fileStatuses={fileStatuses}
                scenario={scenario}
                fileInfo={files?.[0]}
                currentFY={currentFY}
                selectedMonth={selectedMonth}
                fileType={fileType}
              />
            </Box>
          </Flex>
        </ModalBody>

        <ModalFooter bg={footerBg} borderTop="1px" borderColor={borderColor} py={1}>
          <Flex w="100%" justify="space-between" align="center">
            <HStack spacing={2}>
              <Button
                variant="ghost"
                onClick={onClose}
                size="md"
                isDisabled={approveLoading || rejectLoading || excelLoading || isRefreshing}
              >
                Cancel
              </Button>
              {selectedTalukaIds.length > 0 && (
                <Tooltip label="Clear all selections">
                  <IconButton
                    icon={<RepeatIcon />}
                    size="sm"
                    variant="ghost"
                    onClick={clearSelection}
                    aria-label="Clear selection"
                    isDisabled={approveLoading || rejectLoading || excelLoading || isRefreshing}
                  />
                </Tooltip>
              )}
            </HStack>

            <ActionButtons
              selectedFileIds={selectedFileIds}
              selectedFiles={selectedFiles}
              hasUnsavedChanges={hasUnsavedChanges}
              approveLoading={approveLoading}
              rejectLoading={rejectLoading}
              showRejectInput={showRejectInput}
              rejectionComment={rejectionComment}
              onRejectToggle={handleRejectToggle}
              onRejectCommentChange={(e) => setRejectionComment(e.target.value)}
              onRejectSubmit={handleReject}
              onApprove={handleApprove}
              onSave={handleSave}
              onDiscard={handleDiscard}
              isSaving={excelLoading}
              successColor={successColor}
              dangerColor={dangerColor}
              validateFilesForAction={validateFilesForAction}
            />
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ExcelEditModal;